
import React from 'react';
import { Calendar, User } from 'lucide-react';
import { ReadingTime } from '@/components/blog/ReadingTime';
import { SocialShareButtons } from '@/components/blog/public/SocialShareButtons';
import type { Database } from '@/integrations/supabase/types';

type BlogPost = Partial<Database['public']['Tables']['blog_posts']['Row']> & {
  author?: {
    first_name?: string;
    last_name?: string;
  } | null;
  tags?: { id: string; name: string }[];
};

interface BlogPostHeaderProps {
  post: BlogPost;
  formattedDate: string;
  authorName: string;
  readingTimeMinutes: number;
  currentUrl: string;
  isNavigating: boolean;
}

export function BlogPostHeader({
  post,
  formattedDate,
  authorName,
  readingTimeMinutes,
  currentUrl,
  isNavigating
}: BlogPostHeaderProps) {
  return (
    <div className="mb-8">
      <h1 className={`text-3xl md:text-4xl font-bold tracking-tight mb-4 ${isNavigating ? 'opacity-70' : ''}`}>
        {post.title}
      </h1>
      
      <div className={`flex flex-wrap gap-4 items-center text-sm text-muted-foreground mb-6 ${isNavigating ? 'opacity-70' : ''}`}>
        {formattedDate && (
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <time dateTime={post.created_at || ''}>{formattedDate}</time>
          </div>
        )}
        
        <div className="flex items-center gap-1">
          <User className="h-4 w-4" />
          <span>{authorName}</span>
        </div>
        
        {readingTimeMinutes > 0 && (
          <ReadingTime minutes={readingTimeMinutes} />
        )}
      </div>
      
      <div className="my-4 social-share">
        <SocialShareButtons 
          title={post.title} 
          url={currentUrl}
          description={post.excerpt || ''}
          image={post.featured_image || ''}
        />
      </div>
      
      {post.featured_image && (
        <div className={`aspect-video rounded-lg overflow-hidden mb-8 ${isNavigating ? 'opacity-70' : ''}`}>
          <img 
            src={post.featured_image} 
            alt={post.title} 
            className="w-full h-full object-cover"
          />
        </div>
      )}
    </div>
  );
}
